/**
 * The workflow's periods as one strip (DOP-625), oldest on the left, with the
 * one on screen highlighted and "New period" at the end.
 *
 * It replaced a dropdown in the header: a menu hides every quarter but the
 * one you are on, and the question someone opening a monitoring workflow is
 * asking is "which quarter am I in, and what came before" — so every period
 * is visible at once, each with its dates. It sits at the top of the tabs
 * that read a period's board; Borrowers and the setup do not depend on the
 * period and do not show it.
 *
 * Lock state is shown only where it is KNOWN: a period is "locked" when its
 * board says every borrower's run is locked, and only the selected period's
 * board is loaded. Every other period shows a neutral date — "Ended …" once
 * its period is over — never "due", which would read as still open when it
 * may be locked. (Lock state for every period needs it on the periods list.)
 */

import { Lock, Plus } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import type { MonitoringPeriod } from "../../types/monitoring";
import { formatIsoDate } from "./monitoringFormat";
import NewPeriodForm, { type PeriodCreateHandler } from "./NewPeriodForm";

/** Today as YYYY-MM-DD in local time, to compare with the ISO period dates. */
const todayIso = (): string => {
	const now = new Date();
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * A period's line under its label. "Locked" only when known; otherwise dates:
 * a period that has ended says so, one still running says when packages are due.
 */
export const periodStatusLine = (period: MonitoringPeriod, locked: boolean, today = todayIso()): string => {
	if (locked) return "Locked";
	if (period.period_end < today) return `Ended ${formatIsoDate(period.period_end)}`;
	return `Packages due ${formatIsoDate(period.package_due)}`;
};

interface PeriodStripProps {
	periods: MonitoringPeriod[];
	selectedId: string | null;
	/** Public ids of periods whose every borrower is locked. */
	lockedIds: ReadonlySet<string>;
	onSelect: (periodId: string) => void;
	/** Must not reject; a failure comes back as `{ ok: false }`. */
	onCreate: PeriodCreateHandler;
}

const PeriodStrip: React.FC<PeriodStripProps> = ({
	periods,
	selectedId,
	lockedIds,
	onSelect,
	onCreate,
}) => {
	const [formOpen, setFormOpen] = useState(false);
	const formRef = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);

	// A click outside closes the new-period form.
	useEffect(() => {
		if (!formOpen) return;
		const close = (event: MouseEvent) => {
			if (!formRef.current?.contains(event.target as Node)) setFormOpen(false);
		};
		document.addEventListener("mousedown", close);
		return () => document.removeEventListener("mousedown", close);
	}, [formOpen]);

	// Many quarters overflow sideways; start scrolled to the newest, which is
	// the one being worked on.
	useEffect(() => {
		const el = scrollRef.current;
		if (el) el.scrollLeft = el.scrollWidth;
	}, [periods.length]);

	return (
		<div className="mb-5 flex items-stretch gap-2" data-tour="period-strip">
			<div
				ref={scrollRef}
				role="radiogroup"
				aria-label="Period"
				className="flex min-w-0 items-stretch gap-2 overflow-x-auto"
			>
				{periods.map((period) => {
					const isSelected = period.public_id === selectedId;
					const locked = lockedIds.has(period.public_id);
					return (
						<button
							key={period.public_id}
							type="button"
							role="radio"
							aria-checked={isSelected}
							onClick={() => onSelect(period.public_id)}
							className={`flex min-w-[132px] shrink-0 flex-col items-start gap-0.5 rounded-xl border px-3.5 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] ${
								isSelected
									? "border-[#004C4B] bg-[#E6F3F3] shadow-[inset_0_0_0_1px_#004C4B]"
									: "border-gray-200 bg-white hover:border-[#99CFCD] hover:bg-[#f6fbfb]"
							}`}
						>
							<span
								className={`flex items-center gap-1.5 text-sm font-semibold ${
									isSelected ? "text-[#004C4B]" : "text-[#111111]"
								}`}
							>
								{period.label}
								{locked && <Lock className="h-3.5 w-3.5" aria-label="Locked" />}
							</span>
							<span className={`text-xs ${isSelected ? "text-[#2f6f6d]" : "text-[#6B7280]"}`}>
								{periodStatusLine(period, locked)}
							</span>
						</button>
					);
				})}
			</div>

			<div className="relative shrink-0" ref={formRef}>
				<button
					type="button"
					onClick={() => setFormOpen((was) => !was)}
					aria-expanded={formOpen}
					className="flex h-full min-h-[52px] items-center gap-1.5 rounded-xl border border-dashed border-gray-300 bg-white px-3.5 text-sm font-medium text-[#004C4B] hover:border-[#99CFCD] hover:bg-[#f6fbfb] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
				>
					<Plus className="h-4 w-4" aria-hidden />
					New period
				</button>
				{formOpen && (
					<div className="absolute right-0 z-20 mt-1 w-80 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
						<NewPeriodForm
							onCreate={onCreate}
							onCancel={() => setFormOpen(false)}
							onDone={() => setFormOpen(false)}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default PeriodStrip;
